import type { ClusterQA } from "@/lib/qa/cluster-pages";

/**
 * Adapters that map service-specific Q&A shapes onto the shared ClusterQA
 * shape so the topic sub-hub renderer can split a 1,000+ link hub into
 * light per-topic pages (better LCP, less PageRank dilution).
 */

type InterpreterLike = { slug: string; q: string; a: string; synonyms: string[]; scenarioSlug?: string };
type ScenarioLike = { slug: string; th: string };

export function interpreterQaAsCluster(
  qa: readonly InterpreterLike[],
  scenarios: readonly ScenarioLike[],
): ClusterQA[] {
  const label = new Map(scenarios.map((s) => [s.slug, s.th]));
  return qa.map((item) => ({
    slug: item.slug,
    q: item.q,
    a: item.a,
    synonyms: item.synonyms,
    topicSlug: item.scenarioSlug && label.has(item.scenarioSlug) ? item.scenarioSlug : "general",
    topicTh: (item.scenarioSlug && label.get(item.scenarioSlug)) || "คำถามทั่วไปเรื่องล่าม",
  }));
}

type MltLike = { slug: string; q: string; aShort: string; synonyms: string[]; cluster: string };

export const MLT_CLUSTER_LABELS: Record<string, string> = {
  definition: "นิยาม / แปลรับรองคืออะไร",
  price: "ค่าบริการและการขอใบเสนอราคา",
  speed: "ระยะเวลา / งานด่วน",
  process: "ขั้นตอนการทำงาน",
  documents: "เอกสารที่ต้องเตรียม",
  language: "คู่ภาษา × ประเภทเอกสาร",
  authority: "หน่วยงานปลายทาง",
  certification: "การรับรอง Notary / กงสุล / Apostille",
  compare: "เปรียบเทียบเส้นทางรับรอง",
  specialized: "งานแปลเฉพาะทาง",
  troubleshoot: "ปัญหาที่พบบ่อยและการแก้ไข",
};

export function mltQaAsCluster(qa: readonly MltLike[]): ClusterQA[] {
  return qa.map((item) => ({
    slug: item.slug,
    q: item.q,
    a: item.aShort,
    synonyms: item.synonyms,
    topicSlug: item.cluster,
    topicTh: MLT_CLUSTER_LABELS[item.cluster] ?? "คำถามทั่วไป",
  }));
}

type LabelledTopic = { slug: string; nameTh?: string; th?: string };
type SimpleQa = { slug: string; q: string; a: string; synonyms: string[]; topicSlug?: string };

/**
 * Generic adapter: joins a cluster's Q&A rows to its topic list so the shared
 * topic sub-hub renderer can split one huge link hub into light pages.
 */
export function qaWithTopicLabels(
  qa: readonly SimpleQa[],
  topics: readonly LabelledTopic[],
  fallbackTh: string,
): ClusterQA[] {
  const label = new Map(topics.map((t) => [t.slug, t.nameTh ?? t.th ?? fallbackTh]));
  return qa.map((item) => ({
    slug: item.slug,
    q: item.q,
    a: item.a,
    synonyms: item.synonyms,
    topicSlug: item.topicSlug && label.has(item.topicSlug) ? item.topicSlug : "general",
    topicTh: (item.topicSlug && label.get(item.topicSlug)) || fallbackTh,
  }));
}

type ShortQa = { slug: string; q: string; aShort: string; synonyms?: string[]; cluster: string };

/**
 * Adapter for the "500+ questions" clusters (visa, pcc, moj, sworn, apostille)
 * whose rows carry a `cluster` key plus a short answer. Maps them onto the
 * shared ClusterQA shape so one huge hub can be split into topic sub-hubs.
 */
export function shortQaAsCluster(
  qa: readonly ShortQa[],
  labels: Record<string, string>,
  fallbackTh = "คำถามทั่วไป",
): ClusterQA[] {
  return qa.map((item) => ({
    slug: item.slug,
    q: item.q,
    a: item.aShort,
    synonyms: item.synonyms ?? [],
    topicSlug: labels[item.cluster] ? item.cluster : "general",
    topicTh: labels[item.cluster] ?? fallbackTh,
  }));
}

type ServiceQa = { slug: string; q: string; a: string; synonyms?: string[]; serviceSlug?: string };

/** Adapter for clusters that group Q&A by `serviceSlug` (immigration, longstay). */
export function serviceQaAsCluster(
  qa: readonly ServiceQa[],
  services: readonly LabelledTopic[],
  fallbackTh: string,
): ClusterQA[] {
  const label = new Map(services.map((s) => [s.slug, s.nameTh ?? s.th ?? fallbackTh]));
  return qa.map((item) => ({
    slug: item.slug,
    q: item.q,
    a: item.a,
    synonyms: item.synonyms ?? [],
    topicSlug: item.serviceSlug && label.has(item.serviceSlug) ? item.serviceSlug : "general",
    topicTh: (item.serviceSlug && label.get(item.serviceSlug)) || fallbackTh,
  }));
}
